// Panel: every section of every screen (TEMPLATES.md, DETAIL_LEVELS.md part 4).
//
// The title is required. A section with no name is the bug this component
// exists to fix, so the type makes it impossible.
//
// Chrome is tier-aware: on a phone the screen title already frames the
// section, so the box goes away and the pixels go to content. One component,
// not a second mobile design to keep in sync.

import { useId, useState, type CSSProperties, type ReactNode } from 'react';
import { IconLayoutList, IconTable } from '@tabler/icons-react';
import { opensAsSheet } from '@fsp/theme';
import { useDetail } from '../data/detail';
import { useTier } from '../shell/useTier';

export type PanelView = 'zen' | 'sheet';

export interface PanelProps {
  /** Required: every section says what it is. */
  title: string;
  /** A quiet figure or link on the right of the header. */
  meta?: ReactNode;
  /** The zen body. */
  children: ReactNode;
  /**
   * The spreadsheet body. Passing it puts the zen/spreadsheet switch in the
   * header — the control belongs where its effect is. The panel opens on
   * whichever pole the detail level picks, and the manager can flip it.
   */
  sheet?: ReactNode;
  /** Fill the available height and scroll inside, rather than sizing to content. */
  fill?: boolean;
  className?: string;
  style?: CSSProperties;
  /** A class for the body: the padding a panel's content wants. */
  bodyClass?: string;
  /**
   * Drawn in the head in place of the title, which stays the section's name
   * for a screen reader: a switcher that already names what is showing (My
   * Team's side panel, U21), where a title over it would say it twice.
   */
  heading?: ReactNode;
  /**
   * Drawn over the body, in its place, while the body keeps its scroll under
   * it: a player's card opened from a list in My Team's side panel (B-316),
   * so Back finds the list where it was. The body is hidden from a screen
   * reader and the keyboard while it is covered. A panel that can be covered
   * passes `null` while nothing is over it, never leaves the prop out, so its
   * body keeps its place in the tree.
   */
  over?: ReactNode | null;
  /**
   * A how-to beside the title, read when asked: an `InfoButton` saying what
   * the section is and how it plays (pick'em, B-484). Not with `heading`.
   */
  help?: ReactNode;
}

export function Panel({
  title,
  meta,
  children,
  sheet,
  fill = false,
  className,
  style,
  bodyClass,
  heading,
  over,
  help,
}: PanelProps) {
  const tier = useTier();
  const detail = useDetail();
  const headingId = useId();
  // Per panel, and sticky per panel: a calm lineup can sit beside a dense
  // free-agent table on one screen.
  const [view, setView] = useState<PanelView>(() => (opensAsSheet(detail) ? 'sheet' : 'zen'));
  const showSheet = sheet !== undefined && view === 'sheet';

  return (
    <section
      className={`fsp-panel ${className ?? ''}`}
      data-chrome={tier === 'phone' ? 'light' : 'full'}
      data-fill={fill ? '' : undefined}
      aria-labelledby={headingId}
      style={style}
    >
      <div className="fsp-panel-head" data-heading={heading !== undefined || undefined}>
        {help !== undefined && heading === undefined ? (
          <div className="fsp-panel-named">
            <h2 className="fsp-panel-title" id={headingId}>
              {title}
            </h2>
            {help}
          </div>
        ) : (
          <h2 className={heading !== undefined ? 'fsp-sr' : 'fsp-panel-title'} id={headingId}>
            {title}
          </h2>
        )}
        {heading}
        <div className="fsp-panel-tools">
          {meta}
          {sheet !== undefined ? <ViewSwitch view={view} onChange={setView} title={title} /> : null}
        </div>
      </div>
      {over === undefined ? (
        <div className={`fsp-panel-body ${bodyClass ?? ''}`}>{showSheet ? sheet : children}</div>
      ) : (
        // ONE SHAPE, COVERED OR NOT: a panel that can be covered always draws
        // the stack, so laying something over the body and taking it away
        // never moves the body in the tree. When it moved, React mounted the
        // list afresh and Back found it at the top with its state gone (the
        // review of 2026-10-13).
        <div className="fsp-panel-stack">
          <div
            className={`fsp-panel-body ${bodyClass ?? ''}`}
            data-covered={over !== null || undefined}
          >
            {showSheet ? sheet : children}
          </div>
          {over !== null ? <div className="fsp-panel-over">{over}</div> : null}
        </div>
      )}
    </section>
  );
}

export function ViewSwitch({
  view,
  onChange,
  title,
}: {
  view: PanelView;
  onChange: (v: PanelView) => void;
  title: string;
}) {
  return (
    <div className="fsp-viewswitch" role="group" aria-label={`How to show ${title}`}>
      <button
        type="button"
        className="fsp-viewswitch-btn"
        aria-pressed={view === 'zen'}
        onClick={() => onChange('zen')}
        title="Just the essentials"
      >
        <IconLayoutList size={15} stroke={1.9} />
        <span className="fsp-sr">Just the essentials</span>
      </button>
      <button
        type="button"
        className="fsp-viewswitch-btn"
        aria-pressed={view === 'sheet'}
        onClick={() => onChange('sheet')}
        title="Every column"
      >
        <IconTable size={15} stroke={1.9} />
        <span className="fsp-sr">Every column</span>
      </button>
    </div>
  );
}
